import { Link, Paper, Typography } from '@material-ui/core'
import CountDownTimer from '../portico/count-down-timer/count-down-timer'
import { LoadingOnBackdrop } from '../portico/loading/loading'
import NavButton from '../portico/button/button'
import Spacing from '../portico/spacing/spacing'
import ErrorMessage from '../portico/error/error-message'
import React, { useEffect, useState } from 'react'
import OtpInput from 'react-otp-input'
import { useDispatch } from 'react-redux'
import STATIC_TEXT from '../../content/common.json'
import { triggetOTP, validateOTL, validateOTP } from '../../services/identity-service'
import { setSnackBar } from '../../stores/ui/ui-action'
import { invalidOTLAction } from '../../stores/user/user-action'
import CONSTANTS from '../../utils/constants'
import { getBrowserCDDC } from '../../utils/identity-utils'
import useMFAStyles from './mfa.styles'
import CustomLoading from '../custom/custom-loading'
import { login } from '../../stores/async/identity-async-actions'
import useRouteNavigator from '../../shared/hooks/use-route-navigator'
import { useUser } from '../../stores/user/user.hooks'

interface MFADialogProps {
    otlToken?: string
    idToken?: any
    showMFAPopup?: boolean
    setShowMFAPopup?: Function
    phoneNumber?: any
    sessionAliveDuration?: number
}
export const MFADialog: React.FC<MFADialogProps> = ({ otlToken, idToken, showMFAPopup, phoneNumber }) => {
    const dispatch = useDispatch()

    const [otp, setOtp] = useState<string>()
    const [otpError, setOtpError] = useState<boolean>()
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [otpFocused, setOtpFocused] = useState<boolean>(false)

    // State variables for resend functionality
    const [resendCodeSubmitting, setResendCodeSubmitting] = useState<any>()
    const [resendCodeCount, setResendCodeCount] = useState<number>(0)

    // State variable that handles showing the resend link and the countdown timer
    const [showResendCode, setShowResendCode] = useState<boolean>(true)

    const [showCannotVerifyMessage, setShowCannotVerifyMessage] = useState<boolean>(false)

    const { determineRouteNavigation } = useRouteNavigator()

    useEffect(() => {
        if (otp && otp.length === 6) validateOtp();
        return () => {};
    }, [otp])

    useEffect(() => {
        const focusFirstInput = () => {
            if (!otpFocused && showMFAPopup) {
                var otpInput = document.querySelectorAll('[data-testid="otp-input-field-0"]')
                ;(otpInput[0] as HTMLElement)?.focus()
                setOtpFocused(true)
            }
        }

        // Use a timeout to wait for the OTPInput component to render
        const timeoutId = setTimeout(focusFirstInput, 100)

        // Clear the timeout on component unmount to avoid memory leaks
        return () => clearTimeout(timeoutId)
    })

    const classes = useMFAStyles()
    const retryLimit = CONSTANTS.MFA.RESEND_CODE_RETRIES

    /**
     * Function to handle the resend code button click
     */
    const handleResendCodeClick = async () => {
        // Do nothing and return
        if (resendCodeCount >= retryLimit) return

        // Increment the retry count
        setResendCodeCount(resendCodeCount + 1)

        // Validate if OTL Token is still valid and Trigger OTP
        try {
            const { countryCode, number } = phoneNumber
            setResendCodeSubmitting(true)
            await validateOTL(otlToken)
            await triggetOTP(otlToken, idToken, countryCode, number, getBrowserCDDC())

            // Toggle the countdown timer, after the OTP is triggered
            setShowResendCode(false)
            setResendCodeSubmitting(false)
        } catch {
            dispatch(invalidOTLAction(true))
            setResendCodeSubmitting(false)
        }
    }

    /**
     * Function to handle the "Can't verify using text message?" link
     */
    const handleCannotVerifyClick = () => {
        setShowCannotVerifyMessage(true)
    }

    /**
     * Function to handle the "Try again" event
     */
    const handleTryAgain = () => {
        setOtp('')
        setShowCannotVerifyMessage(false)
    }

    /**
     * Handled that is called when timer is expired
     */
    const timerExpired = () => {
        // Show the resend code link (that hides the timer)
        setShowResendCode(true)
    }

    const user: any = useUser()
    useEffect(() => {
        if (user.user_role && user.actions) {
            determineRouteNavigation(user.user_role, user.actions)
        }
    }, [user])

    const validateOtp = async () => {
        setOtpError(false)
        if (!otp) return
        setSubmitting(true)
        try {
            const resp = await validateOTP(otlToken, idToken, otp, getBrowserCDDC())

            if (resp) {
                const jwtToken = resp.jwtToken ? resp.jwtToken : resp
                const i2c_item = resp?.i2c_item

                setOtp(undefined)
                setOtpError(false)
                setSubmitting(false)
                dispatch(login(jwtToken))
            }
        } catch (e) {
            setOtp(undefined)
            setOtpError(true)
            setSubmitting(false)
            dispatch(setSnackBar({ show: true, message: 'Verification Failed', messageType: 'error' }))
        }
    }

    const getLast4DigitsPhoneNumber = () => {
        return phoneNumber ? `${phoneNumber.number?.substr(-4)}` : ''
    }

    /**
     * Function that returns the content to be rendered
     *
     * With-in retry limit, if the user clicks the resend link then the countdown timer
     * will be shown and at the end of the countdown, the resend link will be shown
     * handled by this condition ( !showResendCode && (resendCodeCount < retryLimit) )
     *
     * On the retry limit, we are not showing the countdown timer, we just disable the link
     * handled by this condition (showResendCode ||  (resendCodeCount >= retryLimit) )
     * @returns resend code link or count down timer
     */
    const toggleTimerControl = () => {
        if (!showResendCode && resendCodeCount < retryLimit) {
            return (
                <span className={classes.resendCodeWrapper}>
                    {STATIC_TEXT.mfa.codeSentMsg}
                    <CountDownTimer
                        startSeconds={CONSTANTS.MFA.COUNT_DOWN_START_TIME_SECONDS}
                        countDownIntervalInSeconds={CONSTANTS.MFA.COUNT_DOWN_INTERVAL_SECONDS}
                        timeExpired={timerExpired}
                        countDownMessage={STATIC_TEXT.mfa.countDownMsg}
                    />
                </span>
            )
        } else if (showResendCode || resendCodeCount >= retryLimit) {
            return (
                <Link
                    component="button"
                    className={classes.linkButton}
                    data-cy="resend-code-btn"
                    disabled={resendCodeCount >= retryLimit}
                    onClick={handleResendCodeClick}
                >
                    {STATIC_TEXT.mfa.resendCodeBtnText}
                </Link>
            )
        }
    }

    return (
        <>
            {submitting && <CustomLoading message="Verifying Code..." />}
            <LoadingOnBackdrop status={resendCodeSubmitting} message="Triggering OTP..." />
            {showMFAPopup && (
                <Paper elevation={3} className={`${classes.wrapper} ${showCannotVerifyMessage && classes.canNotVerifyWrapper}`}>
                    <Typography variant="subtitle2" style={{ color: '#757575' }}>
                        {STATIC_TEXT.mfa.subTitle}
                    </Typography>

                    {!showCannotVerifyMessage && (
                        <>
                            <Spacing variable="spacing-base" />
                            <Typography variant="h1" style={{ fontSize: '1.875rem' }}>
                                {STATIC_TEXT.mfa.title}
                            </Typography>
                            <Spacing variable="spacing-base-x2" />

                            <Typography variant="body1">
                                {STATIC_TEXT.mfa.phoneNumberInfo}
                                <span className={classes.mfaPhoneLabel}> {getLast4DigitsPhoneNumber()}.</span>
                            </Typography>

                            <Spacing variable="spacing-base-x3" />
                            <Typography variant="body1" className={classes.mfaLabel}>
                                {STATIC_TEXT.mfa.otpLabel}
                            </Typography>

                            <Spacing variable="spacing-base-x2" />
                            <OtpInput
                                inputStyle={classes.otpInput}
                                numInputs={6}
                                onChange={(value) => {
                                    setOtp(value)
                                }}
                                value={otp}
                                renderInput={(props, index) => <input {...props} data-testid={'otp-input-field-' + index} />}
                            />
                            {otpError ? <ErrorMessage message={STATIC_TEXT.mfa.otpError} /> : null}

                            <Spacing variable="spacing-base-x2" />

                            <div>{toggleTimerControl()}</div>

                            <div>
                                <Link
                                    component="button"
                                    className={classes.linkButton}
                                    data-cy="cannot-verify-btn"
                                    onClick={handleCannotVerifyClick}
                                >
                                    {STATIC_TEXT.mfa.cannotVerify}
                                </Link>
                            </div>
                            <Spacing variable="spacing-base-x3" />
                            <NavButton
                                dataCy="verify-btn"
                                buttonLabel="Verify"
                                onNext={validateOtp}
                                disableButton={!(otp && otp.length === 6)}
                            />
                        </>
                    )}
                    {showCannotVerifyMessage && (
                        <>
                            <Spacing variable="spacing-base" />
                            <Typography variant="h1">{STATIC_TEXT.mfa.title}</Typography>
                            <Spacing variable="spacing-base-x2" />

                            <Typography variant="body1">{STATIC_TEXT.mfa.canNotVerifyMFA.sorryMsg}</Typography>
                            <Spacing variable="spacing-base-x2" />
                            <Typography variant="body1">
                                <span>{STATIC_TEXT.mfa.canNotVerifyMFA.contactMsgPartialOne}</span>
                                <a href={`mailto:${CONSTANTS.LENDX_SUPPORT_EMAIL}`} target="_blank" className={classes.emailSupport}>
                                    {' '}
                                    {CONSTANTS.LENDX_SUPPORT}{' '}
                                </a>
                                <span>{STATIC_TEXT.mfa.canNotVerifyMFA.contactMsgPartialTwo}</span>
                            </Typography>
                            <Spacing variable="spacing-base-x3" />
                            <NavButton dataCy="return-to-login-btn" buttonLabel={CONSTANTS.TRY_AGAIN} onNext={handleTryAgain} />
                        </>
                    )}
                </Paper>
            )}
        </>
    )
}
